import { BlobCtaButton, CardGlass, Modal, SelectField, Typography, useToast } from "@cupcodev/ui";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { EMPLOYEE_RANGES, type CompanyProfileInput, type EmployeeRange } from "../../shared/profile/company-profile";
import { NaveIcon, Notice } from "../../ui/NavePage";
import { useI18n } from "../i18n/I18nProvider";
import { useScenario } from "../scenario/ScenarioProvider";

const FORM_ID = "company-profile-form";
// Dismissing ("later" or closing) keeps the prompt away for 30 days; saving keeps it away for good on this browser,
// even if the server still reports optional fields as missing.
const SNOOZE_MS = 30 * 24 * 60 * 60 * 1000;
const DONE = Number.MAX_SAFE_INTEGER;
const snoozeKey = (clientId: string): string => `nave.profilePrompt.snoozedUntil:${clientId}`;
const snoozedUntil = (clientId: string): number => { try { return Number(window.localStorage.getItem(snoozeKey(clientId))) || 0; } catch { return 0; } };
const snooze = (clientId: string, until = Date.now() + SNOOZE_MS): void => { try { window.localStorage.setItem(snoozeKey(clientId), String(until)); } catch { /* storage may be unavailable */ } };

type ProfileCompletion = Readonly<{ pending: boolean; open: () => void }>;
const ProfileCompletionContext = createContext<ProfileCompletion>({ pending: false, open: () => undefined });

/** Whether the active company still has an incomplete profile the user may edit, and a way to open the form. */
export const useProfileCompletion = (): ProfileCompletion => useContext(ProfileCompletionContext);

/**
 * Owns the company-profile form. It opens by itself once (until "remind me later" snoozes it) and
 * stays reachable through `useProfileCompletion().open()` while the profile is incomplete. It only
 * applies to someone allowed to edit the profile; the BFF/owner repeat that check.
 */
export const ProfileCompletionProvider = ({ children }: { children: ReactNode }) => {
  const { runtime, session, activeOrganizationId } = useScenario();
  const { t } = useI18n();
  const { toast } = useToast();
  const clientId = session?.activeClient?.id;
  const [pending, setPending] = useState(false);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [employees, setEmployees] = useState<EmployeeRange | "">("");
  const [segment, setSegment] = useState("");
  const [website, setWebsite] = useState("");
  const [description, setDescription] = useState("");
  const editable = Boolean(clientId && runtime.experience.saveCompanyProfile);

  useEffect(() => {
    let cancelled = false;
    setOpen(false);
    setPending(false);
    if (!clientId || !runtime.experience.getCompanyProfile) return;
    void runtime.experience.getCompanyProfile().then((result) => {
      if (cancelled || !result.ok) return;
      const { profile, complete, canEdit } = result.value;
      if (complete || !canEdit) return;
      setEmployees(profile.employeesRange ?? "");
      setSegment(profile.segment ?? "");
      setWebsite(profile.website ?? "");
      setDescription(profile.description ?? "");
      setPending(true);
      if (snoozedUntil(clientId) <= Date.now()) setOpen(true);
    }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [runtime, clientId, activeOrganizationId.value]);

  const openForm = useCallback(() => { setError(undefined); setOpen(true); }, []);
  const value = useMemo<ProfileCompletion>(() => ({ pending: pending && editable, open: openForm }), [pending, editable, openForm]);

  const later = (): void => { if (clientId) snooze(clientId); setOpen(false); };
  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (busy || employees === "" || !runtime.experience.saveCompanyProfile) return;
    setBusy(true);
    setError(undefined);
    const input: CompanyProfileInput = { employeesRange: employees, segment: segment.trim(),
      ...(website.trim() ? { website: website.trim() } : {}), ...(description.trim() ? { description: description.trim() } : {}) };
    try {
      const result = await runtime.experience.saveCompanyProfile(input);
      if (!result.ok) { setError(result.error.code); return; }
      if (clientId) snooze(clientId, DONE);
      setOpen(false);
      setPending(false);
      toast({ variant: "success", title: t("profile.saved") });
    } catch { setError("DEPENDENCY_UNAVAILABLE"); } finally { setBusy(false); }
  };

  return (
    <ProfileCompletionContext.Provider value={value}>
      {children}
      {editable ? <Modal open={open} onOpenChange={(next) => { if (!busy && !next) later(); }} title={t("profile.title")} description={t("profile.description", { company: session?.activeClient?.name ?? "" })} size="md" layout="compact" closeOnBackdrop={false} showCloseButton
        footer={<div className="nave-modal-actions">
          <BlobCtaButton type="button" size="sm" variant="secondary" disabled={busy} onClick={later}>{t("profile.later")}</BlobCtaButton>
          <BlobCtaButton type="submit" form={FORM_ID} size="sm" loading={busy} disabled={busy || employees === "" || segment.trim().length < 2}>{t("profile.save")}</BlobCtaButton>
        </div>}>
        <form id={FORM_ID} className="nave-form" onSubmit={(event) => void submit(event)}>
          <div className="nave-field">
            <span>{t("profile.employees.label")}</span>
            <SelectField value={employees} onChange={(next) => setEmployees(next as EmployeeRange)} ariaLabel={t("profile.employees.label")} disabled={busy}
              placeholder={t("profile.employees.placeholder")} options={EMPLOYEE_RANGES.map((range) => ({ value: range, label: t(`profile.employees.${range}`) }))} />
          </div>
          <label className="nave-field"><span>{t("profile.segment.label")}</span>
            <input required minLength={2} maxLength={120} value={segment} disabled={busy} placeholder={t("profile.segment.placeholder")} onChange={(event) => setSegment(event.target.value)} />
          </label>
          <label className="nave-field"><span>{t("profile.website.label")}</span>
            <input type="text" inputMode="url" maxLength={200} value={website} disabled={busy} placeholder={t("profile.website.placeholder")} onChange={(event) => setWebsite(event.target.value)} />
          </label>
          <label className="nave-field"><span>{t("profile.about.label")}</span>
            <textarea rows={3} maxLength={600} value={description} disabled={busy} onChange={(event) => setDescription(event.target.value)} />
            <span className="nave-char-count">{description.length}/600</span>
          </label>
          {error ? <Notice tone="warning">{t(error === "VALIDATION_ERROR" ? "profile.error.validation" : error === "FORBIDDEN" ? "profile.error.forbidden" : "profile.error.unavailable")}</Notice> : null}
        </form>
      </Modal> : null}
    </ProfileCompletionContext.Provider>
  );
};

/** Home call-to-action shown while the company profile is incomplete. */
export const ProfileCompletionCard = () => {
  const { pending, open } = useProfileCompletion();
  const { session } = useScenario();
  const { t } = useI18n();
  if (!pending) return null;
  return (
    <CardGlass variant="glass-md" elevation={0} className="nave-profile-cta">
      <span className="nave-profile-cta-icon" aria-hidden="true"><NaveIcon name="lucide:building-2" /></span>
      <span className="nave-profile-cta-copy">
        <Typography as="h2" variant="title-sm">{t("profile.title")}</Typography>
        <Typography as="p" variant="body-sm" tone="muted">{t("profile.description", { company: session?.activeClient?.name ?? "" })}</Typography>
      </span>
      <BlobCtaButton type="button" size="sm" onClick={open}>{t("profile.cta")}</BlobCtaButton>
    </CardGlass>
  );
};
